import cn from 'classnames';
import { parseJSON } from 'date-fns/fp';
import { RefreshIcon } from '#assets/ReactSvg.ts';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import type { Host } from '#dn-types/host.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useTime } from '#hooks/useTime.ts';
import { InlineIconLink } from '#screens/hosts/hosts-table/InlineIconLink.tsx';
import { computeHostStatus } from '#util/hostStatus.ts';
import { relativePastTime } from '#util/time.ts';
import styles from './LastSeenAt.module.css';

type Props = {
  className?: string;
  host: Pick<Host, 'createdAt' | 'id' | 'isLighthouse' | 'isRelay' | 'isBlocked'> & {
    metadata: Pick<Host['metadata'], 'lastSeenAt' | 'platform'>;
  };
  dataUpdatedAt: Date;
};

export function LastSeenAt({ className, host, dataUpdatedAt }: Props) {
  const currentTime = useTime();
  const location = useAppLocation();

  const {
    id,
    metadata: { lastSeenAt },
  } = host;
  const status = computeHostStatus(host, dataUpdatedAt);

  if (status === 'unenrolled') {
    return (
      <div className={cn('flex align-center', className)}>
        Never seen
        <InlineIconLink
          className={styles.LastSeenAt_enrollLink}
          title="Enroll"
          to={`/hosts/${id}/enroll`}
          state={{ from: location }}
        >
          <RefreshIcon />
        </InlineIconLink>
      </div>
    );
  }

  if (status === 'unknown' || !lastSeenAt) {
    return <span className={className}>Unknown</span>;
  }

  if (status === 'online') {
    return (
      <Timestamp className={className} datetime={lastSeenAt}>
        Online
      </Timestamp>
    );
  }

  return (
    <Timestamp className={className} datetime={lastSeenAt}>
      Seen {relativePastTime(parseJSON(lastSeenAt), currentTime, { seconds: 5 })}
    </Timestamp>
  );
}
